import { ExternalToast, toast } from "sonner";
import { TToastVariant } from "@/types/public";
import { isToastSeen, markToastAsSeen } from "@/lib/toastGarbage";

let playToastSound: null | ((v: TToastVariant) => void) = null;
export function setToastSoundPlayer(fn: (v: TToastVariant) => void) {
  playToastSound = fn;
}

function getToastClassNames(variant: TToastVariant = "default") {
  const baseClasses = {
    toast:
      "p-4 rounded-md shadow-md flex gap-2 items-center w-[300px] relative",
    title: "font-semibold",
    description: "text-sm",
    actionButton:
      "ml-4 inline-flex items-center rounded-md px-3 py-1 text-sm font-medium",
    cancelButton: "",
    closeButton: "",
  };

  switch (variant) {
    case "success":
      return {
        ...baseClasses,
        toast: `${baseClasses.toast} bg-success-background border border-success`,
        title: `${baseClasses.title} text-success`,
        description: `${baseClasses.description} text-success`,
        actionButton: `${baseClasses.actionButton} bg-success-background text-white`,
        icon: `${baseClasses.title} text-success`,
      };
    case "error":
      return {
        ...baseClasses,
        toast: `${baseClasses.toast} bg-red-50 border border-destructive`,
        title: `${baseClasses.title} text-destructive`,
        description: `${baseClasses.description} text-red-700`,
        actionButton: `${baseClasses.actionButton} bg-red-500 text-white`,
        icon: `${baseClasses.title} text-destructive`,
      };
    case "info":
      return {
        ...baseClasses,
        toast: `${baseClasses.toast} bg-info-background border border-info`,
        title: `${baseClasses.title} text-info`,
        description: `${baseClasses.description} text-info`,
        actionButton: `${baseClasses.actionButton} bg-info-background text-white`,
        icon: `${baseClasses.title} text-info`,
      };
    default:
      return {
        ...baseClasses,
        toast: `${baseClasses.toast} bg-background border border-gray-200`,
        title: `${baseClasses.title} text-primary`,
        description: `${baseClasses.description} text-gray-800`,
        actionButton: `${baseClasses.actionButton} text-white`,
      };
  }
}

export function toaster(
  variant: TToastVariant = "default",
  title: string,
  options?: ExternalToast,
  ignoreDisplayLimit?: boolean
) {
  const classNames = getToastClassNames(variant);
  const opt = { unstyled: true, classNames, ...options };

  const toastId = opt?.id?.toString();
  if (toastId && isToastSeen(toastId)) {
    return;
  }
  if (toastId && !ignoreDisplayLimit) {
    markToastAsSeen(toastId);
  }

  if (playToastSound) {
    try {
      playToastSound(variant);
    } catch (e) {
      console.error(e);
    }
  }

  switch (variant) {
    case "success":
      return toast.success(title, opt);
    case "error":
      return toast.error(title, opt);
    case "info":
      return toast.info(title, opt);
    default:
      return toast(title, opt);
  }
}
